import { useEffect } from 'react';
import { Modal } from '../Modal';

// Viewer keyboard shortcuts, matching the handlers wired in ResourceViewer.
interface Shortcut { keys: string; description: string }

const NAVIGATION: Shortcut[] = [
  { keys: 'Alt + Q', description: 'Close document' },
  { keys: '←', description: 'Previous document' },
  { keys: '→', description: 'Next document' },
  { keys: '?', description: 'Show this help' },
];

const TAGGING: Shortcut[] = [
  { keys: 'Ctrl + Space', description: "Tag as 'Reviewed'" },
  { keys: 'Alt + R', description: "Tag as 'Responsive'" },
  { keys: 'Alt + N', description: "Tag as 'Not Responsive'" },
  { keys: 'Alt + K', description: "Tag as 'Confidential'" },
  { keys: 'Alt + P', description: "Tag as 'Privileged'" },
  { keys: 'Alt + J', description: "Tag as 'Junk'" },
];

function Section({ title, shortcuts }: { title: string; shortcuts: Shortcut[] }) {
  return (
    <>
      <tr><th colSpan={2} style={{ borderTop: 'none' }}>{title}</th></tr>
      {shortcuts.map((s) => (
        <tr key={s.description}>
          <td style={{ width: 130 }}><kbd>{s.keys}</kbd></td>
          <td>{s.description}</td>
        </tr>
      ))}
    </>
  );
}

/** Keyboard-shortcuts help dialog — the React port of the viewer's Hotkeys button. */
export function HotkeysModal({ canTag, onClose, targetDocument }: { canTag?: boolean; onClose: () => void; targetDocument?: Document }) {
  // Esc closes the dialog. Capture phase + stopPropagation so the viewer's
  // document-level hotkey listener doesn't also see the Escape. Bind to the
  // document the dialog actually lives in (the pop-out window when detached).
  useEffect(() => {
    const doc = targetDocument ?? document;
    function onKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    doc.addEventListener('keydown', onKeydown, true);
    return () => {
      try { doc.removeEventListener('keydown', onKeydown, true); } catch { /* noop */ }
    };
  }, [onClose, targetDocument]);

  return (
    <Modal
      title="Keyboard Shortcuts"
      icon="far fa-keyboard"
      onClose={onClose}
      targetDocument={targetDocument}
      footer={<button type="button" className="btn btn-primary" onClick={onClose}>Close</button>}
    >
      <table className="table">
        <tbody>
          <Section title="Navigation" shortcuts={NAVIGATION} />
          {canTag && <Section title="Tagging" shortcuts={TAGGING} />}
        </tbody>
      </table>
    </Modal>
  );
}
